import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { nanoid } from "nanoid";
import { isEqual, omit } from "lodash";
import { TbSparkles, TbSettings } from "react-icons/tb";
import { useShow } from "@/contexts/ShowContext";
import { useModal } from "@/contexts/ModalContext";
import { apiFetch } from "@/lib/api";
import { uploadBlob } from "@/lib/uploadBlob";
import useFileUpload from "@/hooks/useFileUpload";
import { isPendingUpload } from "@/components/AiModelInput";
import Loader from "@/components/loader";
import SceneAIRenderSettingsModal from "@/components/modals/SceneAIRenderSettingsModal";
import ConfirmModal from "@/components/modals/ConfirmModal";
import Toolbar from "./Toolbar";
import Viewport from "./Viewport";
import AiRenderOverlay from "./AiRenderOverlay";
import ManipulationPanel from "./ManipulationPanel";
import HierarchyPanel from "./HierarchyPanel";
import InspectPanel from "./InspectPanel";
import ProjectionsPanel from "./ProjectionsPanel";
import PropertiesPanel from "./PropertiesPanel";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/shadcn/resizable";
import {
  createNode,
  createDefaultNodes,
  buildNodesFromGLTFScene,
  parseGLTFArrayBuffer,
  primeModelScene,
} from "./sceneGraph";
import {
  childrenOf,
  descendantsOf,
  reorderNode,
  cloneSubtree,
  WORLD_NODE_ID,
} from "./nodeTree";
import { DEFAULT_QUALITY } from "./renderQuality";
import { hashNodes } from "./sceneSnapshot";
import useAiRender from "./aiRender";
import { GEN_AI_MODELS } from "@kagami/shared/sceneRenderModels";

const DEFAULT_SCENE_SETTINGS = {
  aiDescription: "",
  aiReferenceImages: [],
  backgroundColor: "#1a1c20",
  backgroundTextureUrl: null,
  backgroundTextureKey: null,
  ambientLightColor: "#ffffff",
  ambientLightIntensity: 0.15,
  environmentEnabled: true,
  environmentIntensity: 1,
  qualityPreset: DEFAULT_QUALITY,
  gridVisible: true,
  activeCameraId: null,
  freeCamera: null,
  cameraMode: "look",
  genAiModelId: GEN_AI_MODELS[0].model_id,
  aiResolutionKey: GEN_AI_MODELS[0].camera_presets[0]?.resolution ?? null,
};

const isTypingTarget = (el) =>
  !!el &&
  (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) ||
    el.isContentEditable);

const GEOMETRY_LABELS = {
  plane: "Plane",
  cube: "Cube",
  sphere: "Sphere",
  cone: "Cone",
  cylinder: "Cylinder",
};

const LIGHT_TYPE_LABELS = {
  point: "Point Light",
  directional: "Directional Light",
};

// Oldest renders fall off once a scene's history hits this many — keeps the
// scene doc bounded rather than growing forever across a scene's lifetime.
const MAX_AI_RENDERS = 50;

// Undo/redo depth — oldest entries fall off once either stack hits this many.
const HISTORY_LIMIT = 100;
// Continuous property edits (dragging a number field, a slider, a color
// picker, typing) only get a fresh undo checkpoint at most this often — see
// recordHistory below.
const HISTORY_THROTTLE_MS = 1000;

export default function Scene3DEditor({
  sceneId,
  folderId,
  onClose,
  onSaveComplete,
}) {
  const { show } = useShow();
  const { openModal } = useModal();
  const { uploadFile } = useFileUpload();
  const { uploadFile: uploadModelFile, isUploading: importingModel } =
    useFileUpload();
  const [nodes, setNodes] = useState(() => createDefaultNodes());
  const [sceneSettings, setSceneSettings] = useState(DEFAULT_SCENE_SETTINGS);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [propertiesTab, setPropertiesTab] = useState("transform");
  const [sceneName, setSceneName] = useState("Untitled Scene");
  const [dbSceneId, setDbSceneId] = useState(sceneId || null);
  const [loading, setLoading] = useState(!!sceneId);
  const [saving, setSaving] = useState(false);
  const [gizmoMode, setGizmoMode] = useState("translate");
  const [gizmoSpace, setGizmoSpace] = useState("world");
  const [cameraMode, setCameraMode] = useState("look");
  // Which pass the viewport renders — "shaded" | "depth" (see Toolbar's
  // RENDER_PASSES and the renderMode effect in Viewport.jsx). An editor
  // display preference, not scene data, so it's ephemeral like gizmoMode
  // rather than persisted into sceneSettings.
  const [renderMode, setRenderMode] = useState("shaded");
  // Node id -> {color, boundingBox} for every AI-described object, recomputed
  // by Viewport whenever nodes/the piloted camera change (see its
  // onObjectBoundsChange call) — threaded down into InspectPanel so its
  // scene.json preview shows the same data an actual render would send.
  const [objectBoundsById, setObjectBoundsById] = useState({});
  // Node id -> {color, boundingBox} for every AI-described light, recomputed
  // alongside objectBoundsById above — same purpose, just for lights (see
  // computeLightBoundsById in renderPipeline.js).
  const [lightBoundsById, setLightBoundsById] = useState({});
  // scene.json's "environment" preview (ambient fill + every real light's
  // color/intensity/position relative to the camera) — recomputed alongside
  // objectBoundsById/lightBoundsById above (see Viewport.jsx's
  // onEnvironmentInfoChange call / buildEnvironmentInfo in renderPipeline.js).
  const [environmentInfo, setEnvironmentInfo] = useState(null);
  // Which top-level view the sidebar shows — "scene" is today's
  // Hierarchy/Properties split, "inspect" is a placeholder for now.
  const [sidebarTab, setSidebarTab] = useState("scene");
  // Past AI-render Asset docs for this scene, newest first — the DB only
  // stores the ids (Scene3D.aiRenders), but the GET route populates them, so
  // this always holds full assets (url/key/scope/archived/...) straight from
  // that response. `aiViewMode` drives whether the viewport shows the live
  // scene or the render at `aiViewingIndex` (see the toggle button and
  // AiRenderOverlay below).
  const [aiRenders, setAiRenders] = useState([]);
  const [aiViewMode, setAiViewMode] = useState("scene"); // "scene" | "render"
  const [aiViewingIndex, setAiViewingIndex] = useState(0);
  // One entry per aiRenders entry (matched by assetId), newest first — what
  // AiRenderOverlay's "Create Projection" gate compares the live scene
  // against (see canCreateProjectionFor below and Scene3D.js's
  // renderContextSchema).
  const [renderContexts, setRenderContexts] = useState([]);
  // Saved render-projections for this scene (Scene3D.js's projectionSchema) —
  // listed/toggled from World Settings' "Projections" tab (PropertiesPanel.jsx).
  const [projections, setProjections] = useState([]);
  const [creatingProjection, setCreatingProjection] = useState(false);
  const viewportRef = useRef(null);
  // The {nodes, sceneSettings, sceneName, cameraMode} last known to match the
  // DB — null until a never-before-saved scene's first Save, or an existing
  // scene's load, completes. isDirty (below) compares live state against
  // this; null means "nothing persisted yet", which isDirty treats as dirty
  // rather than false-negative-ing a brand new scene into looking saved.
  // Deliberately excludes projections/aiRenders/renderContexts — those are
  // already eagerly persisted on every mutation (see persistProjections/
  // persistAiRenders above), so they're never what the Save button is for.
  const lastSavedSnapshotRef = useRef(null);
  // Bumped every time lastSavedSnapshotRef.current is reassigned (load +
  // handleSave, below). isDirty's useMemo only reruns when something in ITS
  // OWN dependency array gets a new reference — mutating a ref is invisible
  // to that, so without this, saving a scene that doesn't otherwise touch
  // nodes/sceneSettings/sceneName/cameraMode (the common case: no pending
  // reference images to resolve) left the pre-save `isDirty: true` memoized
  // forever, re-prompting "Unsaved Changes" on the very next Close/back even
  // though the save had already gone through.
  const [savedSnapshotVersion, setSavedSnapshotVersion] = useState(0);
  // Set (in handleSave, below) only at the exact moment a save just resolved
  // a brand-new scene's id — NOT a general "matches dbSceneId" check: dbSceneId
  // is seeded from `sceneId` at mount (see its own useState above), so for an
  // EXISTING scene opened straight from Assets, dbSceneId === sceneId is true
  // on the very first render too, before anything has ever been fetched. A
  // guard keyed on that equality skipped the real load for every
  // existing-scene open, not just the post-save echo it was meant to catch.
  // This ref instead only ever holds a value in the one render right after
  // Scene3DPage's URL update echoes a save back in as a `sceneId` prop change.
  const justSavedSceneIdRef = useRef(null);

  useEffect(() => {
    if (!sceneId || !show) return;
    // Scene3DPage just replaced the URL after a first save (new -> real id)
    // — not a genuine navigation to a different scene — so there's nothing
    // to fetch. Re-running this unconditionally would refetch the document
    // we just wrote and stomp any edits made since that save completed.
    if (sceneId === justSavedSceneIdRef.current) {
      justSavedSceneIdRef.current = null;
      return;
    }
    setLoading(true);
    apiFetch(`/api/shows/${show._id}/scenes3d/${sceneId}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) return;
        const loadedSceneName = data.name || "Untitled Scene";
        const loadedNodes = data.nodes?.length ? data.nodes : nodes;
        const loadedSceneSettings = data.sceneSettings
          ? { ...DEFAULT_SCENE_SETTINGS, ...data.sceneSettings }
          : sceneSettings;
        const loadedCameraMode = data.sceneSettings?.cameraMode || cameraMode;
        setSceneName(loadedSceneName);
        if (data.nodes?.length) setNodes(data.nodes);
        if (data.sceneSettings) {
          setSceneSettings(loadedSceneSettings);
          if (data.sceneSettings.cameraMode) setCameraMode(loadedCameraMode);
        }
        if (data.aiRenders?.length) setAiRenders(data.aiRenders);
        if (data.renderContexts?.length) setRenderContexts(data.renderContexts);
        // `visible` coalesced for scenes saved before the layer-stack
        // redesign — those projections predate the field entirely and
        // should still default to on, same as a freshly created one.
        if (data.projections?.length)
          setProjections(
            data.projections.map((p) => ({ ...p, visible: p.visible ?? true })),
          );
        lastSavedSnapshotRef.current = {
          nodes: loadedNodes,
          sceneSettings: loadedSceneSettings,
          sceneName: loadedSceneName,
          cameraMode: loadedCameraMode,
        };
        setSavedSnapshotVersion((v) => v + 1);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    // Keyed on `show?._id` (the only piece of `show` this effect actually
    // uses), not `show` itself — ShowContext.jsx's own load effect sets
    // `show` from a fresh `fetchShow()` response object every time it runs,
    // including a second, data-equivalent call shortly after the first
    // (StrictMode's dev-only double-invoke of its mount effect, with no
    // abort guard there to skip the second one) — so `show` getting a new
    // reference a second or two after mount, with the identical `_id`, was
    // re-running this whole fetch and flashing the Loader back on.
    // `justSavedSceneIdRef` deliberately omitted too: it's a ref read only
    // for the guard above, not state — reading a ref doesn't need to appear
    // in a dependency array (mutating it wouldn't trigger a re-run anyway).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneId, show?._id]);

  // Undo/redo history — plain stacks of past/future {nodes, sceneSettings}
  // snapshots, capped at HISTORY_LIMIT. Refs, not state: every entry is
  // captured synchronously right before a mutation is applied, and reading
  // through a ref (rather than the `nodes`/`sceneSettings` state values
  // directly) means every mutating callback below can record history
  // without needing them in its own dependency array.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const sceneSettingsRef = useRef(sceneSettings);
  sceneSettingsRef.current = sceneSettings;
  const projectionsRef = useRef(projections);
  projectionsRef.current = projections;
  // Populated below, once persistProjections itself is declared (it depends
  // on dbSceneId/show, further down this component) — undo/redo need to call
  // it but are declared first, and a ref sidesteps that ordering entirely:
  // unlike a direct closure reference, reading `.current` at call time (well
  // after this whole component function has finished its render pass and
  // assigned it) never hits the "used before initialization" trap a plain
  // variable would.
  const persistProjectionsRef = useRef(null);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const lastThrottledHistoryPushRef = useRef(0);

  // Snapshots the state as it is right now — what a later undo would
  // restore — before the caller applies its own change. Structural edits
  // (add/delete/reorder/rename/paste, and — same reasoning — creating/
  // deleting/toggling/reordering a projection layer) always get their own
  // checkpoint by passing nothing. `throttled: true` is for the high-frequency
  // case — dragging a number field, a slider, a color picker, typing in a
  // text field — where every single onChange call getting its own undo step
  // would make undo useless (one Ctrl+Z per pixel of drag); those only get
  // a fresh checkpoint once per HISTORY_THROTTLE_MS, so a whole burst of
  // rapid edits collapses into one step. Either way, taking a new action
  // clears the redo stack, same as any other editor — an old "future"
  // doesn't make sense to reapply once a new edit has been made.
  const recordHistory = useCallback((throttled = false) => {
    redoStackRef.current = [];
    const now = Date.now();
    if (
      throttled &&
      now - lastThrottledHistoryPushRef.current < HISTORY_THROTTLE_MS
    ) {
      return;
    }
    lastThrottledHistoryPushRef.current = now;
    undoStackRef.current.push({
      nodes: nodesRef.current,
      sceneSettings: sceneSettingsRef.current,
      projections: projectionsRef.current,
    });
    if (undoStackRef.current.length > HISTORY_LIMIT) {
      undoStackRef.current.shift();
    }
  }, []);

  // A restored `nodes` array may no longer contain whatever was selected
  // (undoing an add, or redoing a delete) — dropped rather than left
  // dangling, since PropertiesPanel/HierarchyPanel both key off `nodes`
  // still containing `selectedNodeId`.
  const dropStaleSelection = useCallback((restoredNodes) => {
    setSelectedNodeId((id) =>
      id && id !== WORLD_NODE_ID && !restoredNodes.some((n) => n._id === id)
        ? null
        : id,
    );
  }, []);

  const undo = useCallback(() => {
    const entry = undoStackRef.current.pop();
    if (!entry) return;
    // So the very next edit — even an otherwise-throttled one — always gets
    // its own fresh checkpoint instead of silently merging into whatever
    // history was already accumulating before this undo/redo happened.
    lastThrottledHistoryPushRef.current = 0;
    redoStackRef.current.push({
      nodes: nodesRef.current,
      sceneSettings: sceneSettingsRef.current,
      projections: projectionsRef.current,
    });
    if (redoStackRef.current.length > HISTORY_LIMIT)
      redoStackRef.current.shift();
    setNodes(entry.nodes);
    setSceneSettings(entry.sceneSettings);
    // Unlike nodes/sceneSettings (only written to the server by an explicit
    // Save), projections are eagerly persisted on every direct mutation (see
    // handleCreateProjection/handleDeleteProjection/etc.) — an undo/redo that
    // changes them has to eagerly persist too, or a reload right afterward
    // would silently revert to whatever was last persisted. Reference check
    // (not a deep compare) is enough: every projection mutator always
    // produces a fresh array, so an undo that didn't touch projections lands
    // back on the exact same reference and this is a no-op.
    if (entry.projections !== projectionsRef.current) {
      setProjections(entry.projections);
      persistProjectionsRef.current?.(entry.projections);
    }
    dropStaleSelection(entry.nodes);
  }, [dropStaleSelection]);

  const redo = useCallback(() => {
    const entry = redoStackRef.current.pop();
    if (!entry) return;
    lastThrottledHistoryPushRef.current = 0;
    undoStackRef.current.push({
      nodes: nodesRef.current,
      sceneSettings: sceneSettingsRef.current,
      projections: projectionsRef.current,
    });
    if (undoStackRef.current.length > HISTORY_LIMIT)
      undoStackRef.current.shift();
    setNodes(entry.nodes);
    setSceneSettings(entry.sceneSettings);
    if (entry.projections !== projectionsRef.current) {
      setProjections(entry.projections);
      persistProjectionsRef.current?.(entry.projections);
    }
    dropStaleSelection(entry.nodes);
  }, [dropStaleSelection]);

  // New nodes drop in right after whatever's currently selected, under that
  // same parent — so building up a hierarchy doesn't require a drag-reparent
  // after every add. No selection just appends at the root, as before.
  // Shared by addNode (one new node) and handleImportModel (a whole
  // GLB-shaped subtree) — both only need to decide where the *one* new root
  // node they're adding lands among its new siblings.
  const getInsertionPoint = useCallback(() => {
    const selected = nodes.find((n) => n._id === selectedNodeId);
    const parent = selected ? selected.parent : null;
    const siblings = childrenOf(nodes, parent);
    const insertIndex = selected
      ? siblings.findIndex((n) => n._id === selected._id) + 1
      : siblings.length;
    return { parent, insertIndex };
  }, [nodes, selectedNodeId]);

  // Builds the node and selects it outside of setNodes' updater (rather than
  // the usual `setNodes(prev => ...)` form) because createNode()'s random id
  // and the setSelectedNodeId side effect need to run exactly once — Strict
  // Mode's dev-time double-invocation of updater functions would otherwise
  // mint two different ids and select the one that loses the race.
  const addNode = useCallback(
    (type, overrides = {}) => {
      const { parent, insertIndex } = getInsertionPoint();
      const node = createNode(type, {
        parent,
        order: nodes.length,
        ...overrides,
      });
      recordHistory();
      setNodes(reorderNode([...nodes, node], node._id, parent, insertIndex));
      setSelectedNodeId(node._id);
    },
    [nodes, getInsertionPoint, recordHistory],
  );

  // Uploads eagerly, unlike aiReferenceImages' deferred-to-save pattern —
  // the model has to actually be at a real URL before sceneGraph.js can fetch
  // and render it, so there's nothing useful a node could hold until the
  // upload finishes anyway. Parsing happens client-side too (off the same
  // bytes, before or in parallel with the upload) so the file's hierarchy
  // becomes a tree of Scene3D nodes (see buildNodesFromGLTFScene) — one per
  // mesh in the file — instead of one opaque blob.
  const handleImportModel = useCallback(
    async (file) => {
      try {
        // Most browsers don't have ".glb" in their built-in MIME database,
        // so `file.type` off a native file picker is usually "" — and
        // useFileUpload sends that straight through as both the signed-URL
        // request's `fileType` (rejected as missing if empty) and the PUT's
        // Content-Type header. Re-wrapping the same bytes in a File that
        // does carry a type sidesteps both without touching the shared hook.
        const upload = file.type
          ? file
          : new File([file], file.name, { type: "model/gltf-binary" });
        const [result, scene] = await Promise.all([
          uploadModelFile(upload, "scenes3d/models"),
          file.arrayBuffer().then(parseGLTFArrayBuffer),
        ]);
        if (!result?.url) return;
        // The render path (sceneGraph.js) would otherwise re-fetch+re-parse
        // this exact file over the network the moment the new nodes render —
        // it already exists right here, parsed from the same bytes.
        primeModelScene(result.url, scene);

        const { parent, insertIndex } = getInsertionPoint();
        const newNodes = buildNodesFromGLTFScene(scene, {
          modelUrl: result.url,
          modelKey: result.key,
          name: file.name.replace(/\.glb$/i, "") || "Model",
          parent,
          order: nodes.length,
        });
        const wrapper = newNodes[0];
        recordHistory();
        setNodes(
          reorderNode(
            [...nodes, ...newNodes],
            wrapper._id,
            parent,
            insertIndex,
          ),
        );
        setSelectedNodeId(wrapper._id);
      } catch (err) {
        console.error("Model import failed:", err);
      }
    },
    [uploadModelFile, nodes, getInsertionPoint, recordHistory],
  );

  // Root + every descendant of whatever's copied, root first — ids are NOT
  // regenerated here, that happens at paste time (see pasteClipboard/
  // cloneSubtree) so pasting the same copy repeatedly produces distinct
  // nodes each time instead of colliding on the same ids. Safe to hold a
  // plain reference rather than cloning: nothing in this editor ever
  // mutates a node in place, only replaces it via setNodes, so the objects
  // sitting here can't be corrupted by later edits to the live tree.
  const clipboardRef = useRef(null);

  const copySelected = useCallback(() => {
    if (!selectedNodeId || selectedNodeId === WORLD_NODE_ID) return;
    const node = nodes.find((n) => n._id === selectedNodeId);
    if (!node) return;
    clipboardRef.current = [node, ...descendantsOf(nodes, node._id)];
  }, [nodes, selectedNodeId]);

  // Pastes right after whichever node is selected right now — the just-
  // copied node itself, most often, but any other selection works too,
  // same insertion rule as addNode/handleImportModel above (see
  // getInsertionPoint).
  const pasteClipboard = useCallback(() => {
    const clipboard = clipboardRef.current;
    if (!clipboard?.length) return;
    const { parent, insertIndex } = getInsertionPoint();
    const pasted = cloneSubtree(clipboard);
    const root = pasted[0];
    recordHistory();
    setNodes(reorderNode([...nodes, ...pasted], root._id, parent, insertIndex));
    setSelectedNodeId(root._id);
  }, [nodes, getInsertionPoint, recordHistory]);

  // Throttled: driven by PropertiesPanel's onChange, fired on every keystroke
  // or drag-tick of a number field/slider/color picker, not just on commit —
  // see recordHistory's own comment for why that needs coalescing. Committed
  // synchronously, not batched — PropertiesPanel's controls (the AI
  // Description textarea in particular) are fully controlled with no local
  // draft state of their own, so deferring this commit desyncs the DOM's
  // value from React's, which resets the caret to the end on the next render
  // that corrects it. Viewport's own rebuild effect instead skips entirely
  // for edits that don't need a rebuild at all (see its rebuildNodes memo).
  const updateNode = useCallback(
    (nodeId, partial) => {
      recordHistory(true);
      setNodes((prev) =>
        prev.map((n) => (n._id === nodeId ? { ...n, ...partial } : n)),
      );
    },
    [recordHistory],
  );

  const renameNode = useCallback(
    (nodeId, name) => {
      recordHistory();
      setNodes((prev) =>
        prev.map((n) => (n._id === nodeId ? { ...n, name } : n)),
      );
    },
    [recordHistory],
  );

  // Throttled for the same reason as updateNode above — World's own settings
  // (ambient intensity, environment intensity, ...) go through the same
  // continuously-firing PropertiesPanel controls.
  const updateSceneSettings = useCallback(
    (partial) => {
      recordHistory(true);
      setSceneSettings((prev) => ({ ...prev, ...partial }));
    },
    [recordHistory],
  );

  const toggleVisible = useCallback(
    (nodeId) => {
      recordHistory();
      setNodes((prev) =>
        prev.map((n) =>
          n._id === nodeId ? { ...n, visible: n.visible === false } : n,
        ),
      );
    },
    [recordHistory],
  );

  // Deleting a group takes its whole subtree with it, matching how "Null"
  // groups are meant to be used — ungrouping-in-place isn't in scope for v1.
  const deleteNode = useCallback(
    (nodeId) => {
      recordHistory();
      setNodes((prev) => {
        const toRemove = new Set([nodeId]);
        let changed = true;
        while (changed) {
          changed = false;
          for (const n of prev) {
            if (n.parent && toRemove.has(n.parent) && !toRemove.has(n._id)) {
              toRemove.add(n._id);
              changed = true;
            }
          }
        }
        return prev.filter((n) => !toRemove.has(n._id));
      });
      setSelectedNodeId((id) => (id === nodeId ? null : id));
    },
    [recordHistory],
  );

  // No confirmation modal — Ctrl/Cmd+Z undoes a delete same as any other
  // change (see the undo/redo history above), so a blocking "are you sure"
  // dialog on every Delete keypress is no longer buying any real safety.
  const deleteSelected = useCallback(() => {
    if (!selectedNodeId || selectedNodeId === WORLD_NODE_ID) return;
    deleteNode(selectedNodeId);
  }, [selectedNodeId, deleteNode]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (isTypingTarget(document.activeElement)) return;
      e.preventDefault();
      deleteSelected();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [deleteSelected]);

  const handleReorder = useCallback(
    (draggedId, newParentId, newIndex) => {
      recordHistory();
      setNodes((prev) => reorderNode(prev, draggedId, newParentId, newIndex));
    },
    [recordHistory],
  );

  const handleSelectTab = useCallback((nodeId, tab) => {
    setSelectedNodeId(nodeId);
    setPropertiesTab(tab);
  }, []);

  // Committed by the viewport's gizmo (drag end) and keyboard fly navigation
  // (key release) — a partial transform (e.g. just `position`) merged into
  // the node's existing transform rather than replacing it wholesale. This
  // fires once per drag/fly gesture (TransformControls' own "dragging-changed"
  // only reports on release, not continuously), so it always gets its own
  // checkpoint rather than going through the throttled path.
  const handleTransformChange = useCallback(
    (nodeId, partialTransform) => {
      recordHistory();
      setNodes((prev) =>
        prev.map((n) =>
          n._id === nodeId
            ? { ...n, transform: { ...n.transform, ...partialTransform } }
            : n,
        ),
      );
    },
    [recordHistory],
  );

  // Reference-image pickers hold local blob previews (via MiniAssetPicker's
  // pending-upload wrapper) until save, mirroring AiModelInput's own
  // deferred-upload pattern — resolves one list's still-pending entries into
  // real uploaded URLs. Returns the same array reference when nothing was
  // pending, so callers can tell "did this change" with `!==` instead of a
  // separate flag. Shared by node-level aiReferenceImages (mesh and null
  // nodes both carry them — see PropertiesPanel's AI tab) and the World
  // settings' own aiReferenceImages below.
  const resolveReferenceImageList = useCallback(
    async (images) => {
      if (!images?.some(isPendingUpload)) return images;
      const resolved = await Promise.all(
        images.map(async (img) => {
          if (!isPendingUpload(img)) return img;
          const result = await uploadFile(img.file, "scenes3d/references");
          return result?.url
            ? { url: result.url, thumbnail_url: result.thumbnail_url }
            : null;
        }),
      );
      return resolved.filter(Boolean);
    },
    [uploadFile],
  );

  const resolvePendingReferenceImages = useCallback(
    async (nodeList) => {
      let changed = false;
      const resolved = await Promise.all(
        nodeList.map(async (n) => {
          const images = await resolveReferenceImageList(n.aiReferenceImages);
          if (images === n.aiReferenceImages) return n;
          changed = true;
          return { ...n, aiReferenceImages: images };
        }),
      );
      return changed ? resolved : nodeList;
    },
    [resolveReferenceImageList],
  );

  const uploadThumbnail = (blob) =>
    uploadBlob(blob, {
      folder: "scenes3d",
      fileName: `${dbSceneId || "scene3d"}-${Date.now()}.png`,
    });

  // The `aiRenders` id list otherwise only reaches the DB via the scene's
  // own manual "Save" — without this, closing (or reopening) the editor
  // before the next full save would silently lose a just-generated render.
  // So this pushes just the ids immediately (Scene3D.aiRenders is a plain
  // ref array — see Scene3D.js), independent of whether the rest of the
  // scene has unsaved edits. Skipped for a never-yet-saved scene
  // (`dbSceneId` null) — there's no scene3d document to attach it to yet; it
  // still reaches the DB on that scene's first real Save, same as before.
  // `nextContexts` defaults to the current renderContexts state so callers
  // that only changed `aiRenders` itself (e.g. handleSaveAiRenderAsAsset)
  // don't have to pass it through unchanged.
  const persistAiRenders = useCallback(
    async (nextRenders, nextContexts = renderContexts) => {
      if (!dbSceneId || !show) return;
      try {
        await apiFetch(`/api/shows/${show._id}/scenes3d/${dbSceneId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            aiRenders: nextRenders.map((a) => a._id),
            renderContexts: nextContexts,
          }),
        });
      } catch (err) {
        console.error("Failed to persist render history:", err);
      }
    },
    [dbSceneId, show, renderContexts],
  );

  // A finished render always jumps to the front of the history and into
  // view — the toggle then lets the user step back to the live scene
  // without losing their place in the history (aiViewingIndex is left
  // alone if they navigate back to an older one before generating again).
  // `context` (see aiRender.js's generate()) is tagged with the new asset's
  // id and kept alongside it, newest first, same ordering as `aiRenders` —
  // it's what canCreateProjectionFor below checks the live scene against.
  const handleAiRenderGenerated = useCallback(
    (asset, context) => {
      const next = [asset, ...aiRenders].slice(0, MAX_AI_RENDERS);
      const nextContexts = [
        { ...context, assetId: asset._id },
        ...renderContexts,
      ].slice(0, MAX_AI_RENDERS);
      setAiRenders(next);
      setRenderContexts(nextContexts);
      setAiViewingIndex(0);
      setAiViewMode("render");
      persistAiRenders(next, nextContexts);
    },
    [aiRenders, renderContexts, persistAiRenders],
  );

  const aiRender = useAiRender({
    viewportRef,
    show,
    nodes,
    sceneSettings,
    folderId,
    onGenerated: handleAiRenderGenerated,
  });

  // `saveAsAsset` only changes the asset's own scope/archived server-side —
  // it doesn't know about (or update) the `aiRenders` history array, since
  // that belongs here, not to the hook. Splice its response (the updated
  // asset) back into local state so AiRenderOverlay's derived "already
  // saved" check (canSaveAiRenderAsAsset) reflects the DB immediately.
  const handleSaveAiRenderAsAsset = useCallback(
    async (render) => {
      const updated = await aiRender.saveAsAsset(render);
      if (!updated) return;
      const next = aiRenders.map((r) => (r._id === updated._id ? updated : r));
      setAiRenders(next);
      persistAiRenders(next);
    },
    [aiRender, aiRenders, persistAiRenders],
  );

  // Structural-only hash of the live scene (see sceneSnapshot.js) — memoized
  // on `nodes`' own reference since this editor never mutates it in place,
  // only replaces it via setNodes.
  const currentNodesHash = useMemo(() => hashNodes(nodes), [nodes]);

  // Gates AiRenderOverlay's "Create Projection" button: only enabled while
  // the scene still structurally matches what this specific render was
  // generated from (see aiRender.js's generate(), which records that
  // snapshot into renderContexts the moment the render finishes).
  const canCreateProjectionFor = useCallback(
    (render) => {
      if (!render?._id) return false;
      const ctx = renderContexts.find((c) => c.assetId === render._id);
      return !!ctx && ctx.nodesHash === currentNodesHash;
    },
    [renderContexts, currentNodesHash],
  );

  const persistProjections = useCallback(
    async (next) => {
      if (!dbSceneId || !show) return;
      try {
        await apiFetch(`/api/shows/${show._id}/scenes3d/${dbSceneId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projections: next }),
        });
      } catch (err) {
        console.error("Failed to persist projections:", err);
      }
    },
    [dbSceneId, show],
  );
  persistProjectionsRef.current = persistProjections;

  // Persisted eagerly (like aiRenders/renderContexts) — losing a
  // just-created projection on an unsaved close would cost more than the
  // render it came from. Defaults to visible — every visible projection now
  // renders simultaneously as a stacked layer (ProjectionsPanel.jsx), so
  // there's no separate "turn it on" step the way the old single-active-
  // projection design needed. Prepended, not appended — array index 0 is the
  // front-most/top-of-panel-list layer (see projection.js), so a freshly
  // created projection lands on top of the stack, the same place a new layer
  // lands in an image editor. recordHistory()/undo-redo and the switch back
  // to the shaded scene view both happen only once creation actually
  // succeeds, not on an early return.
  const handleCreateProjection = useCallback(
    async (render) => {
      if (!canCreateProjectionFor(render)) return;
      const ctx = renderContexts.find((c) => c.assetId === render._id);
      if (!ctx) return;
      setCreatingProjection(true);
      try {
        const projection = {
          _id: nanoid(),
          name: `Projection ${projections.length + 1}`,
          cameraNodeId: ctx.cameraNodeId,
          cameraName: ctx.cameraName,
          cameraTransform: ctx.cameraTransform,
          fov: ctx.fov,
          aspect: ctx.aspect,
          textureUrl: render.url,
          textureKey: render.key,
          thumbnailUrl: render.thumbnail_url || render.url,
          createdAt: new Date().toISOString(),
          visible: true,
        };
        recordHistory();
        const next = [projection, ...projections];
        setProjections(next);
        await persistProjections(next);
        aiRender.dismiss();
        setAiViewMode("scene");
      } finally {
        setCreatingProjection(false);
      }
    },
    [
      canCreateProjectionFor,
      renderContexts,
      projections,
      persistProjections,
      recordHistory,
      aiRender,
    ],
  );

  // Flips one projection's layer visibility — eagerly persisted, same
  // pattern as create/delete below, since visibility is now durable layer
  // config the user is stacking deliberately (like a Photoshop layer), not
  // throwaway "flip through and compare" scrubbing the way the old single
  // activeProjectionId was. Undoable, same as any other structural edit.
  const handleToggleProjectionVisibility = useCallback(
    (id) => {
      recordHistory();
      const next = projections.map((p) =>
        p._id === id ? { ...p, visible: !(p.visible ?? true) } : p,
      );
      setProjections(next);
      persistProjections(next);
    },
    [projections, persistProjections, recordHistory],
  );

  // Drag-reorder from ProjectionsPanel — `orderedIds` arrives already in the
  // new storage order (array position IS z-order, index 0 = front-most; see
  // projection.js). Eagerly persisted and undoable, same reasoning as
  // visibility above.
  const handleReorderProjections = useCallback(
    (orderedIds) => {
      recordHistory();
      const byId = new Map(projections.map((p) => [p._id, p]));
      const next = orderedIds.map((id) => byId.get(id)).filter(Boolean);
      setProjections(next);
      persistProjections(next);
    },
    [projections, persistProjections, recordHistory],
  );

  // "+ Camera" (ProjectionsPanel.jsx) — restores the original camera node's
  // transform/fov/aspect to what they were when the projection was captured,
  // and pilots that camera. Camera-move only: layer visibility is entirely
  // the visibility toggle's job now, this no longer touches it. If the
  // original node no longer exists (deleted since), this is a no-op —
  // ProjectionsPanel disables the button for that row instead of guessing a
  // substitute camera.
  const handleProjectAndMoveCamera = useCallback(
    (projection) => {
      const cameraNode = nodes.find(
        (n) => n._id === projection.cameraNodeId && n.type === "camera",
      );
      if (!cameraNode) return;
      recordHistory();
      setNodes((prev) =>
        prev.map((n) =>
          n._id === cameraNode._id
            ? {
                ...n,
                transform: { ...n.transform, ...projection.cameraTransform },
                fov: projection.fov,
                aspect: projection.aspect,
              }
            : n,
        ),
      );
      viewportRef.current?.pilotCamera(cameraNode._id);
    },
    [nodes, recordHistory],
  );

  const handleDeleteProjection = useCallback(
    (id) => {
      recordHistory();
      const next = projections.filter((p) => p._id !== id);
      setProjections(next);
      persistProjections(next);
    },
    [projections, persistProjections, recordHistory],
  );

  const handleBackToScene = useCallback(() => {
    aiRender.dismiss();
    setAiViewMode("scene");
  }, [aiRender]);

  const handleOpenAiSettings = useCallback(() => {
    openModal(
      <SceneAIRenderSettingsModal
        viewportRef={viewportRef}
        nodes={nodes}
        genAiModelId={sceneSettings.genAiModelId}
        aiResolutionKey={sceneSettings.aiResolutionKey}
        onApply={(settings) => updateSceneSettings(settings)}
        onRender={(settings) => {
          updateSceneSettings(settings);
          setAiViewMode("render");
          aiRender.generate(settings);
        }}
      />,
    );
  }, [
    openModal,
    nodes,
    sceneSettings.genAiModelId,
    sceneSettings.aiResolutionKey,
    updateSceneSettings,
    aiRender,
  ]);

  const handleSave = useCallback(async () => {
    if (!show) return;
    setSaving(true);
    try {
      viewportRef.current?.commitPendingTransform();
      const resolvedNodes = await resolvePendingReferenceImages(nodes);
      if (resolvedNodes !== nodes) setNodes(resolvedNodes);
      const resolvedWorldReferenceImages = await resolveReferenceImageList(
        sceneSettings.aiReferenceImages,
      );
      if (resolvedWorldReferenceImages !== sceneSettings.aiReferenceImages) {
        setSceneSettings((prev) => ({
          ...prev,
          aiReferenceImages: resolvedWorldReferenceImages,
        }));
      }
      const blob = await viewportRef.current?.captureThumbnail();
      let imageUrl, imageKey;
      if (blob) {
        const result = await uploadThumbnail(blob);
        imageUrl = result.url;
        imageKey = result.key;
      }

      // The piloted camera and the free camera's live position/rotation are
      // never mirrored into React state as they change (that's every frame
      // during flight) — pulled fresh here instead, the same pull-based
      // pattern as `commitPendingTransform`/`captureThumbnail` above. `null`
      // is a meaningful `getActiveCameraId()` result (Free Camera is active),
      // so this can't use `??` to fall back — that would coalesce a real
      // "Free Camera selected" straight past the stale `sceneSettings` value.
      const viewport = viewportRef.current;
      const body = {
        name: sceneName,
        nodes: resolvedNodes,
        sceneSettings: {
          ...sceneSettings,
          aiReferenceImages: resolvedWorldReferenceImages,
          activeCameraId: viewport
            ? viewport.getActiveCameraId()
            : sceneSettings.activeCameraId,
          freeCamera: viewport
            ? viewport.getFreeCameraTransform()
            : sceneSettings.freeCamera,
          cameraMode,
        },
        aiRenders: aiRenders.map((a) => a._id),
        renderContexts,
        projections,
        imageUrl,
        imageKey,
      };
      const res = dbSceneId
        ? await apiFetch(`/api/shows/${show._id}/scenes3d/${dbSceneId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          })
        : await apiFetch(`/api/shows/${show._id}/scenes3d`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });

      const saved = await res.json();
      // Only the new-scene case (dbSceneId was null going into this save)
      // is about to have its `sceneId` prop change underneath it, once
      // Scene3DPage's onSaveComplete rewrites the URL from .../new to the
      // real id — that's the one render the load effect needs to recognize
      // and skip. A resave of an already-existing scene doesn't touch the
      // URL's scene id at all, so there's nothing for this ref to guard.
      if (!dbSceneId && saved._id) justSavedSceneIdRef.current = saved._id;
      if (saved._id) setDbSceneId(saved._id);
      lastSavedSnapshotRef.current = {
        nodes: resolvedNodes,
        sceneSettings: body.sceneSettings,
        sceneName,
        cameraMode,
      };
      setSavedSnapshotVersion((v) => v + 1);
      if (onSaveComplete && imageUrl) {
        onSaveComplete({
          sceneId: saved._id || dbSceneId,
          imageUrl,
          imageKey,
          name: sceneName,
        });
      }
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    nodes,
    sceneName,
    sceneSettings,
    cameraMode,
    aiRenders,
    renderContexts,
    projections,
    dbSceneId,
    onSaveComplete,
    resolvePendingReferenceImages,
    resolveReferenceImageList,
  ]);

  // Whether the manually-saved fields (nodes/sceneSettings/sceneName/
  // cameraMode) differ from what's actually in the DB — drives the Save
  // button's disabled state and the unsaved-changes navigation guard below.
  // Projections/aiRenders/renderContexts are excluded on purpose; see
  // lastSavedSnapshotRef's own comment.
  //
  // sceneSettings.activeCameraId/freeCamera are excluded from the comparison
  // for the same reason: handleSave pulls them fresh from the live Viewport
  // (`viewport.getActiveCameraId()`/`getFreeCameraTransform()`) straight into
  // the saved snapshot, but never mirrors that back into this `sceneSettings`
  // state — Viewport's own initialActiveCameraId/initialFreeCameraTransform
  // effects are explicitly "apply once at load, then leave alone" (re-firing
  // them after every save would either snap the camera back mid-session or
  // need to be a no-op every single time). Comparing them here would leave
  // `isDirty` permanently stuck true the moment the user ever pilots or
  // nudges the free camera, since the two sides could never agree again —
  // which is exactly what made the Close button keep re-prompting even right
  // after a successful Save.
  const isDirty = useMemo(() => {
    if (!lastSavedSnapshotRef.current) return true;
    const withoutLiveCameraFields = (settings) =>
      omit(settings, ["activeCameraId", "freeCamera"]);
    return !isEqual(
      {
        nodes,
        sceneSettings: withoutLiveCameraFields(sceneSettings),
        sceneName,
        cameraMode,
      },
      {
        ...lastSavedSnapshotRef.current,
        sceneSettings: withoutLiveCameraFields(
          lastSavedSnapshotRef.current.sceneSettings,
        ),
      },
    );
    // savedSnapshotVersion isn't read in the body — it's here purely to force
    // a recompute whenever lastSavedSnapshotRef.current (a ref, invisible to
    // useMemo otherwise) gets reassigned by a load or a save.
  }, [nodes, sceneSettings, sceneName, cameraMode, savedSnapshotVersion]);
  // Read inside the event listeners below instead of closing over `isDirty`
  // directly, so those effects don't need to tear down and resubscribe on
  // every single edit just to see a fresh value.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  // Single source of truth for "about to leave with unsaved changes" —
  // covers the toolbar's own Close button (Scene3DPage's onClose calls
  // navigate(-1)), any in-app <Link> click, and the browser back/forward
  // buttons, since all three are transitions this same data router mediates.
  // Replaces an earlier attempt that manually pushed a dummy history entry to
  // "trap" the back button and stepped back past it on confirm — that entry
  // never got removed once isDirty went false again (no pushState has an
  // inverse), so it permanently sat in the stack and broke the very next
  // plain back-navigation (the browser's or this editor's own Close button)
  // even after a successful save. useBlocker intercepts the transition
  // itself, before the URL/history ever actually changes, so there's no
  // stray entry left behind either way.
  // Memoized, not inlined: this version of react-router's useBlocker
  // re-registers the blocker (and notifies its own subscribers) whenever the
  // predicate's identity changes — an inline arrow function is a fresh
  // identity every render, which re-registers every render, which re-renders
  // this component, which creates another fresh identity — an infinite
  // render loop ("Maximum update depth exceeded"). Keyed on `isDirty` so it's
  // still a new (correct) predicate whenever that actually changes.
  const shouldBlockNav = useCallback(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
    [isDirty],
  );
  const blocker = useBlocker(shouldBlockNav);
  // ModalContext's `openModal` (and `handleSave`, which closes over nodes/
  // sceneSettings/etc. and so gets a new identity on basically every edit)
  // are read through refs instead of sitting in the effect's own dependency
  // array — ModalContext.jsx's openModal is a plain function, not memoized,
  // so it's a fresh identity on every ModalProvider render; calling it below
  // triggers exactly such a render (openModal's own setModalStack), which
  // produced a FRESH openModal, which re-ran this effect, which called
  // openModal again — an infinite "Maximum update depth exceeded" loop,
  // confirmed by an isolated repro (reproduced with an unmemoized fake
  // openModal, resolved by routing through a ref instead).
  const openModalRef = useRef(openModal);
  openModalRef.current = openModal;
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  // Keyed on `blocker.state` (a plain string), not `blocker` itself — the
  // object useBlocker returns is a fresh reference every render regardless of
  // whether `.state` actually changed, so depending on the whole object would
  // re-run this effect (and reopen the modal) on every render it caused,
  // which is the same class of infinite loop as openModal's above — just one
  // level up, in useBlocker's own returned object instead of ModalContext's.
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    let cancelled = false;
    openModalRef
      .current(
        <ConfirmModal
          title="Unsaved Changes"
          message="This scene has unsaved changes. What would you like to do?"
          type="warning"
          buttons={[
            { label: "Discard Changes", type: "secondary", onClick: () => {} },
            {
              label: "Save Changes",
              type: "primary",
              onClick: () => handleSaveRef.current(),
            },
          ]}
        />,
      )
      .then((result) => {
        if (cancelled) return;
        // .proceed()/.reset() continue or cancel the transition useBlocker
        // already intercepted — neither needs (or should trigger) a fresh
        // navigate() call of our own.
        if (result !== undefined) blocker.proceed();
        else blocker.reset();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);

  // Warn on browser refresh / tab close — the one case useBlocker can't
  // cover, since that never goes through the router at all.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // F (focus), G (grid toggle), Cmd/Ctrl+S (save), Cmd/Ctrl+C/V (copy/paste
  // selected node's subtree), Cmd/Ctrl+Z / Cmd/Ctrl+Shift+Z / Ctrl+Y
  // (undo/redo) — kept separate from Viewport.jsx's own keydown listener,
  // which owns the continuous WASD/QE/Arrow fly-movement + 1-4 gizmo-mode
  // hotkeys (see its own per-frame pressedKeysRef machinery); these are
  // one-shot actions this component already has everything in scope to
  // perform directly.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isTypingTarget(document.activeElement)) return;
      const key = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && key === "s") {
        e.preventDefault();
        if (!saving && isDirty) handleSave();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && key === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && key === "y") {
        e.preventDefault();
        redo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && key === "c") {
        // Nothing of ours selected — leave the browser's own copy alone
        // (e.g. selected text elsewhere on the page).
        if (!selectedNodeId || selectedNodeId === WORLD_NODE_ID) return;
        e.preventDefault();
        copySelected();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && key === "v") {
        if (!clipboardRef.current?.length) return; // nothing copied from us yet
        e.preventDefault();
        pasteClipboard();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return; // leave other OS/browser chords alone
      if (key === "f") {
        if (!selectedNodeId || selectedNodeId === WORLD_NODE_ID) return;
        e.preventDefault();
        viewportRef.current?.focusOnSelected();
      } else if (key === "g") {
        e.preventDefault();
        updateSceneSettings({ gridVisible: !sceneSettings.gridVisible });
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    saving,
    isDirty,
    handleSave,
    selectedNodeId,
    sceneSettings.gridVisible,
    updateSceneSettings,
    copySelected,
    pasteClipboard,
    undo,
    redo,
  ]);

  const selectedNode = nodes.find((n) => n._id === selectedNodeId) || null;
  // Imperative, not reactive — reflects whichever camera was piloted as of
  // the last render triggered by something else (e.g. an edit), not a live
  // subscription to Viewport's own internal camera-switch state.
  const activeCameraId = viewportRef.current?.getActiveCameraId() ?? null;
  const aiRenderBusy =
    aiRender.phase === "rendering" || aiRender.phase === "generating";
  // The stacked layer set Viewport actually renders — array order IS z-order
  // (index 0 = front-most/wins overlaps, see projection.js), so this is just
  // a filter, not a reorder. Memoized on `projections` itself so Viewport's
  // scene-graph rebuild effect (keyed on this array's identity) doesn't
  // re-fire on unrelated Scene3DEditor re-renders.
  const visibleProjectionsInOrder = useMemo(
    () => projections.filter((p) => p.visible ?? true),
    [projections],
  );
  // Per-row "+ Camera" eligibility and visibility-toggle default for
  // ProjectionsPanel — computed here (against live `nodes`) rather than
  // inside ProjectionsPanel, so it doesn't need to know how to answer that
  // question itself.
  const projectionsForPanel = projections.map((p) => ({
    ...p,
    visible: p.visible ?? true,
    canMoveCamera: nodes.some(
      (n) => n._id === p.cameraNodeId && n.type === "camera",
    ),
  }));

  return (
    <div
      id="Scene3DEditor"
      className="flex flex-col h-full w-full bg-background relative @container">
      <Toolbar
        name={sceneName}
        onNameChange={setSceneName}
        onSave={handleSave}
        onClose={onClose}
        saving={saving}
        hasUnsavedChanges={isDirty}
      />
      <div className="flex flex-1 min-h-0">
        <div
          id="Scene3DEditor__manipulation"
          className="w-14 border-r border-secondary/40 flex-shrink-0">
          <ManipulationPanel
            gizmoMode={gizmoMode}
            onGizmoModeChange={setGizmoMode}
            gizmoSpace={gizmoSpace}
            onGizmoSpaceChange={setGizmoSpace}
            cameraMode={cameraMode}
            onCameraModeChange={setCameraMode}
            onFocusCamera={() => viewportRef.current?.focusOnSelected()}
            canFocusCamera={
              !!selectedNodeId && selectedNodeId !== WORLD_NODE_ID
            }
            gridVisible={sceneSettings.gridVisible}
            onToggleGrid={(v) => updateSceneSettings({ gridVisible: v })}
            renderMode={renderMode}
            onRenderModeChange={(mode) => {
              setRenderMode(mode);
              // Picking a pass is picking "look at the live 3D scene,
              // rendered this way" — jumping back out of the AI render view
              // is implied, not just a side effect to remember to also
              // trigger elsewhere.
              setAiViewMode("scene");
            }}
            aiViewMode={aiViewMode}
            onToggleAiViewMode={() =>
              setAiViewMode((m) => (m === "render" ? "scene" : "render"))
            }
            hasAiRenders={aiRenders.length > 0}
          />
        </div>
        <ResizablePanelGroup direction="horizontal" className="flex-1 min-w-0">
          <ResizablePanel
            defaultSize={75}
            minSize={40}
            className="min-w-0 relative">
            <Viewport
              ref={viewportRef}
              nodes={nodes}
              selectedNodeId={selectedNodeId}
              onSelectNode={setSelectedNodeId}
              gizmoMode={gizmoMode}
              onGizmoModeChange={setGizmoMode}
              gizmoSpace={gizmoSpace}
              cameraMode={cameraMode}
              onTransformChange={handleTransformChange}
              backgroundColor={sceneSettings.backgroundColor}
              backgroundTextureUrl={sceneSettings.backgroundTextureUrl}
              ambientLightColor={sceneSettings.ambientLightColor}
              ambientLightIntensity={sceneSettings.ambientLightIntensity}
              worldDescription={sceneSettings.aiDescription}
              worldReferenceImages={sceneSettings.aiReferenceImages}
              gridVisible={sceneSettings.gridVisible}
              environmentEnabled={sceneSettings.environmentEnabled}
              environmentIntensity={sceneSettings.environmentIntensity}
              qualityPreset={sceneSettings.qualityPreset}
              renderMode={renderMode}
              onObjectBoundsChange={setObjectBoundsById}
              onLightBoundsChange={setLightBoundsById}
              onEnvironmentInfoChange={setEnvironmentInfo}
              initialActiveCameraId={sceneSettings.activeCameraId}
              initialFreeCameraTransform={sceneSettings.freeCamera}
              visibleProjections={visibleProjectionsInOrder}
            />
            {/* Mirrors Viewport's own camera-picker overlay (top-2 left-2,
                same z-10) on the opposite corner — this panel is the same
                `relative` ancestor both anchor against. */}
            <div
              id="Scene3DEditor__renderTriggerGroup"
              className={`absolute top-2 right-2 z-10 flex items-center rounded-md overflow-hidden ${
                aiRenderBusy ? "opacity-60 pointer-events-none" : ""
              }`}>
              <button
                id="Scene3DToolbar__generateTrigger"
                onClick={() => {
                  setAiViewMode("render");
                  aiRender.generate();
                }}
                disabled={!activeCameraId}
                className="flex items-center gap-1 bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer text-xs p-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
                title={
                  activeCameraId
                    ? "Generate AI Render"
                    : "Select (pilot) a camera first — rendering isn't available from the free camera"
                }>
                <TbSparkles className="w-4 h-4" />
                <span>Render</span>
              </button>
              <button
                id="Scene3DToolbar__settingsTrigger"
                onClick={handleOpenAiSettings}
                disabled={!activeCameraId}
                className="flex items-center gap-1 bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer text-xs p-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
                title={
                  activeCameraId
                    ? "AI Render Settings"
                    : "Select (pilot) a camera first — rendering isn't available from the free camera"
                }>
                <TbSettings className="w-4 h-4" />
              </button>
            </div>
            {aiViewMode === "render" && (
              <AiRenderOverlay
                phase={aiRender.phase}
                errorMessage={aiRender.errorMessage}
                renders={aiRenders}
                currentIndex={aiViewingIndex}
                onPrev={() =>
                  setAiViewingIndex((i) =>
                    Math.min(i + 1, aiRenders.length - 1),
                  )
                }
                onNext={() => setAiViewingIndex((i) => Math.max(i - 1, 0))}
                onSaveAsAsset={() =>
                  handleSaveAiRenderAsAsset(aiRenders[aiViewingIndex])
                }
                savingAsset={aiRender.savingAsset}
                onCreateProjection={() =>
                  handleCreateProjection(aiRenders[aiViewingIndex])
                }
                canCreateProjection={canCreateProjectionFor(
                  aiRenders[aiViewingIndex],
                )}
                creatingProjection={creatingProjection}
                onBackToScene={handleBackToScene}
              />
            )}
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel
            id="Scene3DEditor__sidebar"
            defaultSize={300}
            minSize={250}
            maxSize={550}
            className="border-l border-secondary/40 flex flex-col min-h-0">
            <div
              id="Scene3DEditor__sidebarTabs"
              className="flex items-center border-b border-secondary/40 px-1 flex-shrink-0">
              {[
                { id: "scene", label: "Scene" },
                { id: "projections", label: "Projections" },
                { id: "inspect", label: "Inspect" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  id={`Scene3DEditor__sidebarTabTrigger-${id}`}
                  type="button"
                  onClick={() => setSidebarTab(id)}
                  className={`relative flex items-center gap-1 px-2 py-1.5 -mb-px border-b-2 text-xs cursor-pointer ${
                    sidebarTab === id
                      ? "border-primary text-primary"
                      : "border-transparent text-secondary-foreground/60 hover:text-secondary-foreground"
                  }`}>
                  <span>{label}</span>
                  {/* Same dot HierarchyPanel uses for "this node has AI content" —
                      here it's "this scene has at least one visible projection
                      layer" (a hidden one isn't actually affecting the render,
                      so it shouldn't flag the tab). */}
                  {id === "projections" && projections.length > 0 && (
                    <span
                      className={`px-1 text-[10px] rounded-sm ${visibleProjectionsInOrder.length > 0 ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                      {visibleProjectionsInOrder.length > 0
                        ? visibleProjectionsInOrder.length
                        : projections.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {sidebarTab === "scene" && (
              <ResizablePanelGroup
                direction="vertical"
                className="flex-1 min-h-0">
                <ResizablePanel
                  id="Scene3DEditor__hierarchy"
                  defaultSize={550}
                  minSize={120}
                  className="border-b border-secondary/40 min-h-0">
                  <HierarchyPanel
                    nodes={nodes}
                    selectedNodeId={selectedNodeId}
                    onSelect={setSelectedNodeId}
                    onToggleVisible={toggleVisible}
                    onReorder={handleReorder}
                    onRename={renameNode}
                    activeTab={propertiesTab}
                    onSelectTab={handleSelectTab}
                    onAddMesh={(geometry) =>
                      addNode("mesh", {
                        geometry,
                        name: GEOMETRY_LABELS[geometry] || "Object",
                      })
                    }
                    onImportModel={handleImportModel}
                    importingModel={importingModel}
                    onAddNull={() => addNode("null")}
                    onAddLight={(lightType) =>
                      addNode("light", {
                        lightType,
                        name: LIGHT_TYPE_LABELS[lightType] || "Light",
                      })
                    }
                    onAddCamera={() => addNode("camera")}
                  />
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel
                  id="Scene3DEditor__properties"
                  minSize={120}
                  className="min-h-0">
                  <PropertiesPanel
                    node={selectedNode}
                    isWorldSelected={selectedNodeId === WORLD_NODE_ID}
                    sceneSettings={sceneSettings}
                    onChangeSceneSettings={updateSceneSettings}
                    activeTab={propertiesTab}
                    onSelectTab={setPropertiesTab}
                    onChange={(partial) =>
                      selectedNodeId && updateNode(selectedNodeId, partial)
                    }
                  />
                </ResizablePanel>
              </ResizablePanelGroup>
            )}
            {sidebarTab === "projections" && (
              <ProjectionsPanel
                projections={projectionsForPanel}
                onToggleVisibility={handleToggleProjectionVisibility}
                onReorderProjections={handleReorderProjections}
                onProjectAndMoveCamera={handleProjectAndMoveCamera}
                onDeleteProjection={handleDeleteProjection}
              />
            )}
            {sidebarTab === "inspect" && (
              <InspectPanel
                nodes={nodes}
                sceneSettings={sceneSettings}
                activeCameraId={activeCameraId}
                objectBoundsById={objectBoundsById}
                lightBoundsById={lightBoundsById}
                environmentInfo={environmentInfo}
              />
            )}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      {loading && <Loader status="Loading scene..." />}
    </div>
  );
}
